import { useEffect, useRef, useState } from "react";
import AnchoredPortal from "../../../../components/anchoredPortal/anchoredPortal";

import { PopOverWithClickableOptions } from "@risalabs_frontend_org/oasis-ui-kit";
import { TableCellButtonWithThreeDots } from "../../../../data-model/tableCells";
import { ThreeDotIcon } from "../../../../svg/three-dot-icon";
import { useDropdownPosition } from "./hooks/useDropdownPosition";

interface ButtonWithThreeDotsCellProps {
  value: TableCellButtonWithThreeDots;
  onThreeDotsOptionClick?: (optionId: string, rowId: string) => void;
}

const ButtonWithThreeDotsCell = (props: ButtonWithThreeDotsCellProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const { position, shouldFlipUp } = useDropdownPosition(
    isOpen,
    parentRef,
    menuRef,
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        parentRef.current &&
        menuRef.current &&
        !parentRef.current.contains(event.target as Node) &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleOnOptionsClick = (id: string) => {
    if (props.onThreeDotsOptionClick && props.value.rowId) {
      props.onThreeDotsOptionClick(id, props.value.rowId);
    }
    setIsOpen(false);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.value.disabled) return;
    if (props.onThreeDotsOptionClick && props.value.rowId) {
      props.onThreeDotsOptionClick(props.value.buttonId, props.value.rowId);
    }
  };

  const handleThreeDotsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const hasOptions =
    props.value.threeDotsOptions && props.value.threeDotsOptions.length > 0;

  return (
    <div className="flex flex-row items-center gap-2">
      <div
        onClick={handleButtonClick}
        className={`h-auto w-24 rounded-md px-1 py-2 text-center text-xs font-semibold ${
          props.value.disabled
            ? "bg-black text-gray-200 opacity-50"
            : "cursor-pointer bg-black text-white hover:bg-gray-800"
        }`}
      >
        {props.value.label}
      </div>
      {hasOptions && (
        <div
          className="three-dots-with-clickable-options--container flex h-[22px] w-[22px] items-center justify-center bg-[#F7F9FA] hover:scale-105 hover:cursor-pointer"
          onClick={handleThreeDotsClick}
          ref={parentRef}
        >
          <ThreeDotIcon />
          <AnchoredPortal
            parentRef={parentRef}
            shouldShow={isOpen}
            top={shouldFlipUp ? undefined : (position?.bottom ?? 0) + 5}
            bottom={
              shouldFlipUp
                ? window.innerHeight - (position?.top ?? 0) + 5
                : undefined
            }
            right={position ? window.innerWidth - position.right : 15}
            zIndex={1000}
            children={
              <div ref={menuRef}>
                <PopOverWithClickableOptions
                  options={
                    props.value.threeDotsOptions?.map((opt) => ({
                      text: opt.text,
                      id: opt.id,
                    })) ?? []
                  }
                  onClick={handleOnOptionsClick}
                />
              </div>
            }
          />
        </div>
      )}
    </div>
  );
};

export default ButtonWithThreeDotsCell;
